import { Box, Button, Chip, Paper, Stack } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import type { CallLineStatus } from '../../data/types';
import { date, DEMO_TODAY, money } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { useToast } from '../../ui/feedback';
import { PageHeader, StatCard, StatGrid, StatusChip } from '../../ui/primitives';
import CallDefaultsPanel from './CallDefaultsPanel';
import CallNoticePreview from './CallNoticePreview';

export default function CallDetailPage() {
  const { callId } = useParams();
  const scope = useTenantScope();
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [filter, setFilter] = useState<'ALL' | CallLineStatus>('ALL');
  const call = scope.calls.find((item) => item.id === callId);
  const lines = useMemo(
    () => (call ? call.lines.filter((line) => filter === 'ALL' || line.status === filter) : []),
    [call, filter],
  );
  if (!call) return <Navigate to="/manager/calls" replace />;
  const fund = scope.fundById.get(call.fundId)!;
  const due = sumBy(call.lines, (line) => line.amount);
  const paid = sumBy(call.lines, (line) => line.paid);
  const overdue = call.dueDate < DEMO_TODAY;
  const first = call.lines[0];

  return (
    <>
      <PageHeader
        eyebrow={`${fund.name} · created by ${call.createdBy}${call.approvedBy ? `, approved by ${call.approvedBy}` : ''}`}
        title={
          <>
            {fund.code} Call {call.number} · {call.callPct}% <StatusChip status={call.status} />
          </>
        }
        subtitle={call.purpose}
        actions={
          call.status === 'ISSUED' ? (
            <>
              <Button variant="outlined" onClick={() => toast('Arrears report exported', 'info')}>
                Export arrears
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  dispatch({ type: 'CALL_SEND_REMINDERS', id: call.id });
                  toast('Reminders sent to unpaid holders (email and push)');
                }}
              >
                Send reminders
              </Button>
            </>
          ) : undefined
        }
      />
      <StatGrid>
        <StatCard label="Called" value={money(due)} hint={`${call.lines.length} holders`} />
        <StatCard
          label="Received"
          value={money(paid)}
          hint={due ? `${Math.round((paid / due) * 100)}%` : undefined}
          tone="success"
        />
        <StatCard
          label="Outstanding"
          value={money(due - paid)}
          tone={due - paid > 0 ? (overdue ? 'danger' : 'warning') : 'neutral'}
        />
        <StatCard label="Due" value={date(call.dueDate)} hint={`Issued ${date(call.issueDate)}`} />
      </StatGrid>
      {call.lines.length > 0 && (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', xl: '3fr 2fr' } }}>
          <Paper variant="outlined">
            <Stack
              useFlexGap
              direction="row"
              spacing={1}
              sx={{ p: 2, flexWrap: 'wrap', rowGap: 1 }}
            >
              {(['ALL', 'UNPAID', 'PARTIAL', 'PAID'] as const).map((key) => (
                <Chip
                  key={key}
                  label={`${key === 'ALL' ? 'All' : key.charAt(0) + key.slice(1).toLowerCase()} (${key === 'ALL' ? call.lines.length : call.lines.filter((line) => line.status === key).length})`}
                  color={filter === key ? 'primary' : 'default'}
                  variant={filter === key ? 'filled' : 'outlined'}
                  onClick={() => setFilter(key)}
                />
              ))}
            </Stack>
            <DataTable
              rows={lines}
              rowKey={(row) => row.holderId}
              dense
              defaultSort={{ key: 'amount', direction: 'desc' }}
              columns={[
                {
                  key: 'holder',
                  label: 'Holder',
                  sortValue: (row) => scope.holderById.get(row.holderId)?.name ?? '',
                  render: (row) => scope.holderById.get(row.holderId)?.name,
                },
                {
                  key: 'commitment',
                  label: 'Commitment',
                  align: 'right',
                  hideBelow: 'md',
                  render: (row) => money(row.commitment),
                },
                {
                  key: 'amount',
                  label: 'Due',
                  align: 'right',
                  sortValue: (row) => row.amount,
                  render: (row) => money(row.amount),
                },
                {
                  key: 'paid',
                  label: 'Paid',
                  align: 'right',
                  sortValue: (row) => row.paid,
                  render: (row) => money(row.paid),
                },
                { key: 'crn', label: 'BPAY ref', hideBelow: 'md', render: (row) => row.crn },
                {
                  key: 'status',
                  label: 'Status',
                  render: (row) => (
                    <StatusChip
                      status={overdue && row.status !== 'PAID' ? 'OVERDUE' : row.status}
                    />
                  ),
                },
              ]}
            />
          </Paper>
          {first && (
            <CallNoticePreview
              fund={fund}
              holderName={scope.holderById.get(first.holderId)?.name ?? ''}
              amount={first.amount}
              callPct={call.callPct}
              dueDate={call.dueDate}
              purpose={call.purpose}
              reference={first.crn}
            />
          )}
        </Box>
      )}
      {call.status === 'ISSUED' && (
        <Box sx={{ mt: 2 }}>
          <CallDefaultsPanel call={call} />
        </Box>
      )}
    </>
  );
}
